import { useMemo, useState } from 'react'
import { Box, Checkbox, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ConfigLayout from '../components/configLayout'
import MultiSelectSimple from '../../../../../components/dinamicForm/multiSelectSimple'
import { useStyles } from './configureLinkQuestionnaire.styles'
import { ConfigureLinkQuestionnaireProps } from '../types'
import { useGetQuestionnaires, useGetQuestionsByQuestionnaire } from '../hooks/useQuestionnaires'
import { icons } from '../../../../../utils/icons'
import { Item, onChange } from '../../../../../utils/types/generals'

const ConfigureLinkQuestionnaire = ({
  data,
  onSave,
  onBack,
}: ConfigureLinkQuestionnaireProps) => {
  const { t } = useTranslation()
  const styles = useStyles()

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | ''>(
    data.questionnaireId ?? ''
  )
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>(data.questionIds)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<'all' | 'withActionPlan'>('all')

  const {
    data: questionnaires,
    isLoading: isLoadingQuestionnaires,
    isError: isErrorQuestionnaires,
  } = useGetQuestionnaires()

  const {
    data: questions,
    isLoading: isLoadingQuestions,
    isError: isErrorQuestions,
  } = useGetQuestionsByQuestionnaire(selectedQuestionnaireId ? Number(selectedQuestionnaireId) : null)

  const questionnaireItems: Item[] = useMemo(() => {
    if (!questionnaires) return []
    return questionnaires.map((questionnaire) => ({
      label: questionnaire.name,
      value: questionnaire.id,
    }))
  }, [questionnaires])

  const handleQuestionnaireChange: onChange = (event) => {
    const selected = event.target.value as Item | null
    const nextId = selected ? Number(selected.value) : ''
    setSelectedQuestionnaireId(nextId)
    setSelectedQuestionIds([])
    setSearchTerm('')
    setActiveTab('all')
  }

  const handleTabChange = (tab: 'all' | 'withActionPlan') => {
    setActiveTab(tab)
    setSearchTerm('')
  }

  const handleToggleQuestion = (questionId: number) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    )
  }

  const handleSelectAll = () => {
    setSelectedQuestionIds(filteredQuestions.map((question) => question.id))
  }

  const handleClear = () => {
    const visibleIds = new Set(filteredQuestions.map((question) => question.id))
    setSelectedQuestionIds((prev) => prev.filter((id) => !visibleIds.has(id)))
  }

  const handleSave = () => {
    if (!selectedQuestionnaireId) return
    onSave({
      questionnaireId: Number(selectedQuestionnaireId),
      questionIds: selectedQuestionIds,
    })
  }

  const questionsWithActionPlan = useMemo(() => {
    return (questions ?? []).filter((question) => question.hasActionPlanConfigured)
  }, [questions])

  const filteredQuestions = useMemo(() => {
    const source = activeTab === 'withActionPlan' ? questionsWithActionPlan : (questions ?? [])
    if (!searchTerm.trim()) return source
    const term = searchTerm.toLowerCase()
    return source.filter((question) => question.title.toLowerCase().includes(term))
  }, [questions, questionsWithActionPlan, searchTerm, activeTab])

  const selectedVisibleCount = useMemo(
    () => filteredQuestions.filter((question) => selectedQuestionIds.includes(question.id)).length,
    [filteredQuestions, selectedQuestionIds]
  )

  const isQuestionsListEmpty = !isLoadingQuestions && filteredQuestions.length === 0

  const emptyQuestionsMessage = useMemo(() => {
    if (searchTerm.trim()) {
      return t('actionPlan.manualCreation.linkQuestionnaire.noQuestionsFound')
    }
    if (activeTab === 'withActionPlan') {
      return t('actionPlan.manualCreation.linkQuestionnaire.noQuestionsWithActionPlan')
    }
    return t('actionPlan.manualCreation.linkQuestionnaire.noQuestionsAvailable')
  }, [activeTab, searchTerm, t])

  const hasQuestionnaireSelected = Boolean(selectedQuestionnaireId)
  const hasSelectedQuestions = selectedQuestionIds.length > 0
  const isSaveDisabled = !hasQuestionnaireSelected || !hasSelectedQuestions
  const showEmptyState = !hasQuestionnaireSelected || !questions || questions.length === 0

  const questionnaireSelect = (
    <MultiSelectSimple
      name='questionnaire'
      label={t('actionPlan.manualCreation.linkQuestionnaire.selectQuestionnaireLabel')}
      items={questionnaireItems}
      selected={selectedQuestionnaireId}
      onChange={handleQuestionnaireChange}
      loading={isLoadingQuestionnaires}
      sx={styles.questionnaireSelect}
    />
  )

  return (
    <ConfigLayout
      title={t('actionPlan.manualCreation.linkQuestionnaire.selectQuestionnaireTitle')}
      subtitle={t('actionPlan.manualCreation.linkQuestionnaire.selectQuestionnaireSubtitle')}
      onBack={onBack}
      onSave={handleSave}
      saveDisabled={isSaveDisabled}
      saveLabel={t('actionPlan.manualCreation.linkQuestionnaire.saveAndBack')}
      backLabel={t('actionPlan.manualCreation.linkQuestionnaire.back')}
      headerActions={questionnaireSelect}
    >
      <Box sx={styles.content}>
        {isErrorQuestionnaires && (
          <Typography sx={styles.errorMessage}>
            {t('actionPlan.manualCreation.linkQuestionnaire.errorLoadingQuestionnaires')}
          </Typography>
        )}

        {showEmptyState ? (
          <Box sx={styles.emptyState}>
            <Typography sx={styles.emptyStateText}>
              {t('actionPlan.manualCreation.linkQuestionnaire.noQuestionnaireSelected')}
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.questionsSection}>
            <Box sx={styles.questionsHeader}>
              <Typography sx={styles.questionsTitle}>
                {t('actionPlan.manualCreation.linkQuestionnaire.questionsTitle')}
              </Typography>

              <Box sx={styles.tabsContainer}>
                <Box
                  role='tab'
                  tabIndex={0}
                  aria-selected={activeTab === 'all'}
                  sx={[styles.tab, activeTab === 'all' && styles.tabActive]}
                  onClick={() => handleTabChange('all')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleTabChange('all')
                    }
                  }}
                >
                  {t('actionPlan.manualCreation.linkQuestionnaire.allQuestions')}
                  <Box component='span' sx={styles.tabSubtext}>
                    {t('actionPlan.manualCreation.linkQuestionnaire.allQuestionsSubtitle', {
                      count: questions?.length ?? 0,
                    })}
                  </Box>
                </Box>
                <Box
                  role='tab'
                  tabIndex={0}
                  aria-selected={activeTab === 'withActionPlan'}
                  sx={[styles.tab, activeTab === 'withActionPlan' && styles.tabActive]}
                  onClick={() => handleTabChange('withActionPlan')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleTabChange('withActionPlan')
                    }
                  }}
                >
                  {t('actionPlan.manualCreation.linkQuestionnaire.questionsWithActionPlan')}
                  <Box component='span' sx={styles.tabSubtext}>
                    {t('actionPlan.manualCreation.linkQuestionnaire.questionsWithActionPlanSubtitle', {
                      count: questionsWithActionPlan.length,
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={styles.questionsScrollableContent}>
              <Box sx={styles.questionsToolbar}>
                <Typography sx={styles.questionsSubtitle}>
                  {t('actionPlan.manualCreation.linkQuestionnaire.selectQuestionsTitle')}
                  {selectedVisibleCount > 0 && (
                    <Box component='span' sx={styles.selectedBadge}>
                      {selectedVisibleCount}
                    </Box>
                  )}
                </Typography>

                <Box sx={styles.actions}>
                  <Box
                    role='button'
                    tabIndex={0}
                    sx={styles.actionLink}
                    onClick={handleSelectAll}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleSelectAll()
                      }
                    }}
                  >
                    {t('actionPlan.manualCreation.linkQuestionnaire.selectAll')}
                  </Box>
                  <Box
                    role='button'
                    tabIndex={0}
                    sx={[styles.actionLink, styles.actionLinkDanger]}
                    onClick={handleClear}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleClear()
                      }
                    }}
                  >
                    {t('actionPlan.manualCreation.linkQuestionnaire.clear')}
                  </Box>
                </Box>
              </Box>

              {isErrorQuestions && (
                <Typography sx={styles.errorMessage}>
                  {t('actionPlan.manualCreation.linkQuestionnaire.errorLoadingQuestions')}
                </Typography>
              )}

              <Box sx={styles.questionsList}>
                {isLoadingQuestions ? (
                  <Typography sx={styles.loadingText}>{t('general.loading')}</Typography>
                ) : isQuestionsListEmpty ? (
                  <Box sx={styles.questionsEmptyState}>
                    <Typography sx={styles.questionsEmptyStateText}>
                      {emptyQuestionsMessage}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      placeholder={t('actionPlan.manualCreation.linkQuestionnaire.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      sx={styles.searchField}
                      InputProps={{
                        startAdornment: <Box sx={styles.searchIcon}>{icons('search')}</Box>,
                      }}
                    />
                    {filteredQuestions.map((question) => {
                      const isSelected = selectedQuestionIds.includes(question.id)
                      return (
                        <Box
                          key={question.id}
                          role='button'
                          aria-pressed={isSelected}
                          tabIndex={0}
                          onClick={() => handleToggleQuestion(question.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              handleToggleQuestion(question.id)
                            }
                          }}
                          sx={[styles.questionRow]}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleQuestion(question.id)}
                            onClick={(event) => event.stopPropagation()}
                            color='primary'
                            sx={styles.questionCheckbox}
                          />
                          <Box sx={styles.questionContent}>
                            <Typography sx={styles.questionTitle}>{question.title}</Typography>
                            <Typography sx={styles.questionSection}>
                              {question.sectionTitle || t('actionPlan.manualCreation.linkQuestionnaire.section')}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                  </>
                )}
              </Box>
          </Box>
        </Box>
        )}
      </Box>
    </ConfigLayout>
  )
}

export default ConfigureLinkQuestionnaire
